import { useEffect, useState } from "react";
import { X, Printer, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { openPdfBlob } from "@/lib/openPdf";

const F = ({ label, children }) => (
  <div>
    <label className="label-eyebrow block mb-1.5">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-10 text-sm";

// Print-time contract editor: everything shown here goes on the PDF only.
// It NEVER writes back to the vehicle, so the closing math stays untouched.
export const ContractPrintModal = ({ vehicleId, t, onClose }) => {
  const [form, setForm] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    let on = true;
    api.get(`/vehicles/${vehicleId}`).then((res) => {
      if (!on) return;
      const v = res.data;
      setForm({
        buyer_name: v.buyer_name || "",
        buyer_phone: v.buyer_phone || "",
        payment_method: v.payment_method || "",
        sold_price: v.sold_price || 0,
        down_payment: v.down_payment || 0,
        bank_check_amount: v.bank_check_amount || 0,
        bank_name: v.bank_name || "",
        finance_term_months: v.finance_term_months || 0,
        finance_installment_amount: v.finance_installment_amount || 0,
        payment_frequency: v.payment_frequency || "monthly",
        warranty_months: v.warranty_months || 0,
        charge_doc_fee: v.charge_doc_fee !== false,
        doc_fee_amount: v.doc_fee_amount || 499,
        registration_cost: v.registration_cost || 0,
        trade_in_make: v.trade_in_make || "",
        trade_in_model: v.trade_in_model || "",
        trade_in_year: v.trade_in_year || "",
        trade_in_value: v.trade_in_value || 0,
        trade_in_payoff_amount: v.trade_in_payoff_amount || 0,
        trade_in_payoff_bank: v.trade_in_payoff_bank || "",
        lead_source: v.lead_source || "",
        contract_first_payment: v.contract_first_payment || 0,
        contract_credits: [0, 1, 2].map((i) => (v.contract_credits || [])[i] || { amount: "", method: "", date: "" }),
        insurance_transfer_plate: v.insurance_transfer_plate ?? null,
        insurance_transfer_plate_text: v.insurance_transfer_plate_text || "",
        insurance_has: v.insurance_has ?? null,
        insurance_company: v.insurance_company || "",
        contract_additional_info: v.contract_additional_info || "",
      });
    }).catch(() => toast.error(t("error_generic")));
    return () => { on = false; };
  }, [vehicleId]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setCredit = (i, k, v) =>
    setForm((f) => ({
      ...f,
      contract_credits: f.contract_credits.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)),
    }));

  const generate = async () => {
    setGenerating(true);
    try {
      const persist = {
        contract_first_payment: Number(form.contract_first_payment) || 0,
        contract_credits: form.contract_credits
          .filter((cr) => Number(cr.amount) > 0 || cr.method || cr.date)
          .map((cr) => ({ amount: Number(cr.amount) || 0, method: cr.method || "", date: cr.date || "" })),
        insurance_transfer_plate: form.insurance_transfer_plate,
        insurance_transfer_plate_text: form.insurance_transfer_plate_text,
        insurance_has: form.insurance_has,
        insurance_company: form.insurance_company,
        contract_additional_info: form.contract_additional_info,
      };
      try { await api.put(`/vehicles/${vehicleId}`, persist); } catch { /* print-only fallback */ }
      const res = await api.post(`/vehicles/${vehicleId}/purchase-agreement.pdf`, { ...form, ...persist }, { responseType: "blob" });
      openPdfBlob(res.data, "contrato-venda.pdf");
      onClose();
    } catch (e) {
      let msg = t("error_generic");
      try {
        const txt = await e.response.data.text();
        msg = JSON.parse(txt).detail || msg;
      } catch { /* keep generic */ }
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const isFinance = form?.payment_method === "Finance";
  const isCash = form ? (form.payment_method && form.payment_method !== "Finance") : false;
  const docFeeCharged = form ? (isFinance || form.charge_doc_fee) : false;
  const docFee = form && docFeeCharged ? (Number(form.doc_fee_amount) || 0) : 0;
  const regFee = form ? (Number(form.registration_cost) || 0) : 0;
  const carPrice = form ? (Number(form.sold_price) || 0) : 0;
  // À vista: cliente deve o carro inteiro + taxas. Financiado: entrada + 1º pagamento + taxas.
  const totalDebits = form
    ? (isCash ? carPrice : 0) + (Number(form.down_payment) || 0) + (Number(form.contract_first_payment) || 0) + docFee + regFee
    : 0;
  const totalCredits = form ? form.contract_credits.reduce((s, cr) => s + (Number(cr.amount) || 0), 0) : 0;
  const balance = totalDebits - totalCredits;
  const fmt = (n) => `$ ${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const YesNo = ({ value, onChange, testid }) => (
    <div className="flex gap-2">
      <button type="button" data-testid={`${testid}-yes`} onClick={() => onChange(true)}
        className={`px-4 h-9 text-[10px] font-display font-black uppercase tracking-widest border-2 transition-colors ${value === true ? "border-success bg-success text-white" : "border-border text-text-secondary hover:border-success"}`}>
        Sim
      </button>
      <button type="button" data-testid={`${testid}-no`} onClick={() => onChange(false)}
        className={`px-4 h-9 text-[10px] font-display font-black uppercase tracking-widest border-2 transition-colors ${value === false ? "border-primary bg-primary text-white" : "border-border text-text-secondary hover:border-primary"}`}>
        Não
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[80] flex items-start justify-center overflow-auto py-10 px-4">
      <div data-testid="contract-print-modal" className="bg-background border border-border w-full max-w-3xl p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-black text-xl uppercase tracking-tight">
            🖨️ {t("contract_edit_title") || "Contrato de Venda — conferir antes de imprimir"}
          </h2>
          <button type="button" data-testid="contract-modal-close" onClick={onClose}>
            <X size={20} className="text-text-secondary hover:text-primary" />
          </button>
        </div>
        <p className="text-[11px] text-warning border border-warning/40 bg-warning/[0.06] px-3 py-2">
          ⚠️ {t("contract_edit_hint") || "As alterações feitas aqui valem SOMENTE para este PDF — não mudam o fechamento nem os dados do carro."}
        </p>

        {!form ? (
          <div className="py-10 text-center text-text-secondary text-sm">…</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <F label={`👤 ${t("buyer_name") || "Comprador"}`}>
                <input data-testid="cp-buyer" className={inputCls} value={form.buyer_name} onChange={(e) => set("buyer_name", e.target.value)} />
              </F>
              <F label={`📞 ${t("buyer_phone") || "Telefone"}`}>
                <input data-testid="cp-phone" className={inputCls} value={form.buyer_phone} onChange={(e) => set("buyer_phone", e.target.value)} />
              </F>
              <F label={`🚗 ${t("sold_price") || "Preço do carro"} ($)`}>
                <input data-testid="cp-price" type="number" className={inputCls} value={form.sold_price} onChange={(e) => set("sold_price", e.target.value)} />
              </F>
              <F label={`🧾 ${t("net_check_label") || "Net check (financiamento)"} ($)`}>
                <input data-testid="cp-net-check" type="number" className={inputCls} value={form.bank_check_amount} onChange={(e) => set("bank_check_amount", e.target.value)} />
              </F>
              <F label={`💵 ${t("down_payment") || "Entrada"} ($)`}>
                <input data-testid="cp-dp" type="number" className={inputCls} value={form.down_payment} onChange={(e) => set("down_payment", e.target.value)} />
              </F>
              <F label={`🏦 ${t("bank_name_label") || "Banco"}`}>
                <input data-testid="cp-bank" className={inputCls} value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} />
              </F>
              <F label={`📆 ${t("finance_term") || "Qtd. de parcelas"}`}>
                <input data-testid="cp-term" type="number" className={inputCls} value={form.finance_term_months} onChange={(e) => set("finance_term_months", e.target.value)} />
              </F>
              <F label={`💳 ${t("finance_installment") || "Valor da parcela"} ($)`}>
                <input data-testid="cp-installment" type="number" className={inputCls} value={form.finance_installment_amount} onChange={(e) => set("finance_installment_amount", e.target.value)} />
              </F>
              <F label={`🔁 ${t("payment_frequency_label") || "Frequência"}`}>
                <select data-testid="cp-frequency" className={inputCls} value={form.payment_frequency} onChange={(e) => set("payment_frequency", e.target.value)}>
                  <option value="monthly">{t("freq_monthly") || "Mensal"}</option>
                  <option value="weekly">{t("freq_weekly") || "Semanal"}</option>
                </select>
              </F>
              <F label={`🛡️ ${t("warranty_months_label") || "Garantia (meses)"}`}>
                <input data-testid="cp-warranty" type="number" className={inputCls} value={form.warranty_months} onChange={(e) => set("warranty_months", e.target.value)} />
              </F>
              <F label={`📣 ${t("referral_label") || "Referral / Origem"}`}>
                <input data-testid="cp-referral" className={inputCls} value={form.lead_source} onChange={(e) => set("lead_source", e.target.value)} />
              </F>
            </div>

            {/* DOC FEE + REGISTRATION FEE — ambos editáveis, somados no Total Balance */}
            <div data-testid="cp-doc-fee-block" className="border-2 border-info bg-info/[0.08] p-4 space-y-3">
              <p className="label-eyebrow text-info">🧾 DOC FEE + REGISTRATION FEE — entram no Total Balance</p>
              {isFinance ? (
                <p className="text-[12px] text-text-secondary">
                  {t("doc_fee_mandatory") || "Doc fee obrigatório em todo financiamento — sai automático no contrato."}
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="cp-doc-fee-yes"
                    onClick={() => set("charge_doc_fee", true)}
                    className={`h-11 text-[11px] font-display font-black uppercase tracking-widest border-2 transition-colors ${form.charge_doc_fee ? "border-success bg-success text-white" : "border-border text-text-secondary hover:border-success"}`}
                  >
                    ✓ {t("doc_fee_yes") || "Cobrar doc fee"}
                  </button>
                  <button
                    type="button"
                    data-testid="cp-doc-fee-no"
                    onClick={() => set("charge_doc_fee", false)}
                    className={`h-11 text-[11px] font-display font-black uppercase tracking-widest border-2 transition-colors ${!form.charge_doc_fee ? "border-primary bg-primary text-white" : "border-border text-text-secondary hover:border-primary"}`}
                  >
                    ✗ {t("doc_fee_no") || "Não cobrar (desconto)"}
                  </button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <F label="🧾 Doc Fee ($)">
                  <input
                    data-testid="cp-doc-fee-amount"
                    type="number"
                    className={inputCls}
                    disabled={!isFinance && !form.charge_doc_fee}
                    value={form.doc_fee_amount}
                    onChange={(e) => set("doc_fee_amount", e.target.value)}
                  />
                </F>
                <F label="🚗 Registration Fee ($)">
                  <input
                    data-testid="cp-reg-fee"
                    type="number"
                    className={inputCls}
                    value={form.registration_cost}
                    onChange={(e) => set("registration_cost", e.target.value)}
                  />
                </F>
              </div>
            </div>

            {/* DEALER NEGOTIATION — debits, credits e balance calculado */}
            <div data-testid="cp-negotiation-block" className="border border-border p-4 space-y-4">
              <p className="label-eyebrow">💰 Negociação — Débitos & Créditos (sai no Dealer Negotiation)</p>
              <p className="text-[10px] text-text-secondary -mt-2">
                {isCash
                  ? "À vista: Total débitos = Valor do carro + Doc Fee + Registration Fee + 1º pagamento"
                  : "Financiado: Total débitos = Entrada + 1º pagamento + Doc Fee + Registration Fee"}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <F label="💵 Entrada / Down Payment ($)">
                  <input data-testid="cp-neg-dp" type="number" className={inputCls} value={form.down_payment} onChange={(e) => set("down_payment", e.target.value)} />
                </F>
                <F label="1️⃣ Primeiro pagamento ($)">
                  <input data-testid="cp-first-payment" type="number" className={inputCls} value={form.contract_first_payment} onChange={(e) => set("contract_first_payment", e.target.value)} />
                </F>
                <F label="🧾 Tax + Reg + Placa ($)">
                  <input data-testid="cp-neg-reg" type="number" className={inputCls} value={form.registration_cost} onChange={(e) => set("registration_cost", e.target.value)} />
                </F>
              </div>
              {isCash && (
                <p data-testid="cp-cash-carprice-hint" className="text-[11px] text-warning">
                  🚗 Valor do carro incluído nos débitos: {fmt(carPrice)}
                </p>
              )}
              <div className="border border-success/40 bg-success/[0.05] p-3 space-y-2">
                <p className="text-[10px] font-display font-bold uppercase tracking-widest text-success">Créditos (pagamentos já feitos pelo cliente)</p>
                {form.contract_credits.map((cr, i) => (
                  <div key={i} className="grid grid-cols-3 gap-2">
                    <input data-testid={`cp-credit-amount-${i}`} type="number" placeholder="Valor ($)" className={inputCls} value={cr.amount} onChange={(e) => setCredit(i, "amount", e.target.value)} />
                    <input data-testid={`cp-credit-method-${i}`} placeholder="Forma (Cash, Zelle...)" className={inputCls} value={cr.method} onChange={(e) => setCredit(i, "method", e.target.value)} />
                    <input data-testid={`cp-credit-date-${i}`} type="date" className={inputCls} value={cr.date} onChange={(e) => setCredit(i, "date", e.target.value)} />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="border border-primary/40 bg-primary/[0.06] py-2">
                  <p className="text-[9px] uppercase tracking-widest text-text-secondary">Total débitos</p>
                  <p data-testid="cp-total-debits" className="font-display font-black text-primary">{fmt(totalDebits)}</p>
                </div>
                <div className="border border-success/40 bg-success/[0.06] py-2">
                  <p className="text-[9px] uppercase tracking-widest text-text-secondary">Total créditos</p>
                  <p data-testid="cp-total-credits" className="font-display font-black text-success">{fmt(totalCredits)}</p>
                </div>
                <div className="border-2 border-warning bg-warning/[0.08] py-2">
                  <p className="text-[9px] uppercase tracking-widest text-warning">Balance (falta)</p>
                  <p data-testid="cp-balance" className="font-display font-black text-warning">{fmt(balance)}</p>
                </div>
              </div>
            </div>

            {/* INSURANCE — sai automático na folha */}
            <div data-testid="cp-insurance-block" className="border border-border p-4 space-y-3">
              <p className="label-eyebrow">🛡️ Insurance (sai na folha)</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-widest text-text-secondary">Transferir placa?</p>
                  <YesNo value={form.insurance_transfer_plate} onChange={(v) => set("insurance_transfer_plate", v)} testid="cp-plate" />
                  <input data-testid="cp-plate-text" placeholder="Detalhe da placa (opcional)" className={inputCls} value={form.insurance_transfer_plate_text} onChange={(e) => set("insurance_transfer_plate_text", e.target.value)} />
                </div>
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-widest text-text-secondary">Cliente tem seguro?</p>
                  <YesNo value={form.insurance_has} onChange={(v) => set("insurance_has", v)} testid="cp-insurance" />
                  <input data-testid="cp-insurance-company" placeholder="Seguradora (opcional)" className={inputCls} value={form.insurance_company} onChange={(e) => set("insurance_company", e.target.value)} />
                </div>
              </div>
            </div>

            {/* Additional information */}
            <F label="📝 Informações adicionais (sai na folha)">
              <input data-testid="cp-additional-info" className={inputCls} placeholder="Ex: cliente vai trazer o restante sexta-feira..." value={form.contract_additional_info} onChange={(e) => set("contract_additional_info", e.target.value)} />
            </F>

            {/* Trade-in */}
            <div className="border border-warning/30 bg-warning/[0.04] p-4">
              <p className="label-eyebrow text-warning mb-3">🔄 {t("trade_in_title") || "Veículo na troca"}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <F label={t("make") || "Marca"}>
                  <input data-testid="cp-ti-make" className={inputCls} value={form.trade_in_make} onChange={(e) => set("trade_in_make", e.target.value)} />
                </F>
                <F label={t("model") || "Modelo"}>
                  <input data-testid="cp-ti-model" className={inputCls} value={form.trade_in_model} onChange={(e) => set("trade_in_model", e.target.value)} />
                </F>
                <F label={t("year") || "Ano"}>
                  <input data-testid="cp-ti-year" type="number" className={inputCls} value={form.trade_in_year} onChange={(e) => set("trade_in_year", e.target.value)} />
                </F>
                <F label={`💰 ${t("trade_in_value") || "Avaliação"} ($)`}>
                  <input data-testid="cp-ti-value" type="number" className={inputCls} value={form.trade_in_value} onChange={(e) => set("trade_in_value", e.target.value)} />
                </F>
                <F label={`${t("payoff") || "Payoff"} ($)`}>
                  <input data-testid="cp-ti-payoff" type="number" className={inputCls} value={form.trade_in_payoff_amount} onChange={(e) => set("trade_in_payoff_amount", e.target.value)} />
                </F>
                <F label={t("payoff_bank") || "Banco do payoff"}>
                  <input data-testid="cp-ti-bank" className={inputCls} value={form.trade_in_payoff_bank} onChange={(e) => set("trade_in_payoff_bank", e.target.value)} />
                </F>
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-1">
              <button
                type="button"
                data-testid="cp-cancel"
                onClick={onClose}
                className="border border-border px-5 py-2.5 text-[11px] font-display font-bold uppercase tracking-widest hover:border-primary transition-colors"
              >
                {t("cancel") || "Cancelar"}
              </button>
              <button
                type="button"
                data-testid="cp-generate"
                disabled={generating}
                onClick={generate}
                className="bg-primary text-white px-6 py-2.5 text-[11px] font-display font-black uppercase tracking-widest disabled:opacity-50 hover:opacity-90 transition-opacity inline-flex items-center gap-2"
              >
                {generating ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}
                {t("contract_generate") || "Gerar PDF do contrato"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ContractPrintModal;
